import {PIXI} from '@colonist/pixi'
import {CardView} from '../../ui_canvas_engine/CardView'
import {UIGameConstants} from '../UIGameConstants'
import {SectionBgView} from '../../ui_canvas_engine/SectionBgView'
import {UIGameManager} from '../UIGameManager'
import {UIGameCardViewInventory} from './UIGameCardViewInventory'
import {ArrayUtils} from '../../../../../shared/utils/ArrayUtils'
import {UIGameFlickerAnimation} from '../../ui_canvas_engine/animation/UIGameFlickerAnimation'
import {GameColor} from '../../GameColor'
import {UIGameAnimationBase} from '../../ui_canvas_engine/animation/UIGameAnimationBase'
import {UIGameShakeAnimation} from '../../ui_canvas_engine/animation/UIGameShakeAnimation'
import {Point} from '@colonist/game-mechanic-validators'
import {CardEnum} from '@colonist/game-states'

type CardAction = (cardEnum: CardEnum) => void

export class UIGameCardContainerView extends SectionBgView {
    protected readonly uiGameManager: UIGameManager
    readonly cardInventory: UIGameCardViewInventory

    cardH: number
    cardW: number
    cardY: number
    private cardMarginBetweenSameType: number
    private cardMarginBetweenTypes: number = 0
    private readonly initialContainerHeight: number
    private readonly initialContainerWidth: number
    private readonly defaultCardMarginBetweenSameType: number

    private readonly partitionViews: PIXI.Graphics[] = []
    private readonly partitionEnabled: boolean

    private cardAddAction: CardAction = () => undefined
    private cardRemoveAction: CardAction = () => undefined
    private cardClickAction: CardAction = () => undefined
    private cardRightClickAction: CardAction = () => undefined

    private currentlyExpandedCard?: CardView | undefined
    private readonly expandedCardScale = 1.2
    private readonly expandedCardOffsetY = 0.2

    constructor(uiGameManager: UIGameManager, x: number, y: number, w: number, h: number, container: PIXI.Container,
                disableBg: boolean = false, removePadding: boolean = false, partitionEnabled: boolean = true) {
        super(x, y, w, h, container, removePadding)
        this.initialContainerHeight = h
        this.initialContainerWidth = w
        this.defaultCardMarginBetweenSameType = this.height * 0.1 + uiGameManager.canvasController.margin
        this.uiGameManager = uiGameManager
        this.partitionEnabled = partitionEnabled

        this.cardInventory = new UIGameCardViewInventory()
        if(!disableBg) this.drawRoundedBg()

        this.initSizes(removePadding)
    }

    setCardClickAction(action: CardAction) {
        this.cardClickAction = action
    }

    setCardRightClickAction(action: CardAction) {
        this.cardRightClickAction = action
    }

    setCardAddAction(action: CardAction) {
        this.cardAddAction = action
    }

    setCardRemoveAction(action: CardAction) {
        this.cardRemoveAction = action
    }

    private initSizes(removePadding: boolean) {
        const height = this.initialPosition.h
        this.cardH = removePadding ? height * 0.9 : height * 0.8
        this.cardY = removePadding ? height * 0.05 : height * 0.1
        this.cardW = this.cardH / UIGameConstants.CARD_RATIO
        this.cardMarginBetweenSameType = this.defaultCardMarginBetweenSameType
        this.cardMarginBetweenTypes = this.cardW
    }

    addNewCardOfType(cardEnum: CardEnum, animate: boolean = true): CardView {
        const currentCardCount = this.cardInventory.getCardTypeCount(cardEnum) + 1
        const card = new CardView(this.uiGameManager, currentCardCount, this.defaultCardMarginBetweenSameType, this.cardY, this.cardW, this.cardH, cardEnum, this)
        card.view.setClickAction(() => {
            this.cardClickAction(cardEnum)
        })
        card.view.setRightClickAction(() => {
            this.cardRightClickAction(cardEnum)
        })
        this.addCardView(card, animate)
        this.sortChildren()

        return card
    }

    addExistingCard(cardView: CardView, animate: boolean = true) {
        cardView.animationController.animationCompleteCallBack = () => undefined
        cardView.y = this.cardY
        this.addCardView(cardView, animate)
        this.addChild(cardView)
        this.sortChildren()

        const cardCount = this.cardInventory.getCardTypeCount(cardView.cardData.cardEnum)
        cardView.updateBadge(cardCount)
    }

    protected addCardView(cardView: CardView, animate: boolean = true) {
        this.cardInventory.addCardView(cardView)
        this.rebalanceHand(animate)
        this.cardAddAction(cardView.cardData.cardEnum)
        this.addCardExpandFunctionality(cardView)
    }

    private addCardExpandFunctionality(cardView: CardView) {
        if(!cardView.canCardExpand()) return
        if(PIXI.utils.isMobile.any) return

        const hoverAction = () => {
            this.setCardExpanded(cardView.cardData.cardEnum)
        }

        cardView.view.setHoverAction(hoverAction, () => this.resetExpandedCard())
    }

    private resetExpandedCard() {
        if(this.currentlyExpandedCard === undefined) return
        this.currentlyExpandedCard.scale.set(1)
        this.currentlyExpandedCard.position.y = this.cardY
        this.currentlyExpandedCard = undefined
        this.sortChildren()
    }

    private setCardExpanded(cardEnum: CardEnum) {
        const card = this.cardInventory.getTopCardWithType(cardEnum)
        if(card === undefined) return
        if(this.currentlyExpandedCard === card) return

        this.resetExpandedCard()

        this.currentlyExpandedCard = card
        this.currentlyExpandedCard.scale.set(this.expandedCardScale)
        this.currentlyExpandedCard.position.y -= this.cardH * this.expandedCardOffsetY

        this.currentlyExpandedCard.bringCardToFrontOfParent()
    }

    addMultipleCards(cards: CardEnum[], animate: boolean = true): CardView[] {
        const cardViews: CardView[] = []
        for(const card of cards) {
            cardViews.push(this.addNewCardOfType(card, animate))
        }
        return cardViews
    }

    removeCard(cardEnum: CardEnum) {
        const card = this.cardInventory.getTopCardWithType(cardEnum)
        if(card == undefined) return
        card.parent?.removeChild(card)
        ArrayUtils.removeElementFromArray(this.cardInventory.cardViews, card)
        this.rebalanceHand()
        this.cardRemoveAction(card.cardData.cardEnum)
    }

    removeAllCards() {
        this.removeCard(CardEnum.AnyResource)
        for(const cardView of this.cardInventory.cardViews) {
            cardView.parent?.removeChild(cardView)
            this.cardRemoveAction(cardView.cardData.cardEnum)
        }
        this.cardInventory.cardViews = []
        this.rebalanceHand()
    }

    private rebalanceHand(animate: boolean = true) {
        if(this.cardInventory.cardViews.length == 0) return
        if(this.destroyed) return

        const partitionIndexes = this.getCardTypePartitionIndexes()
        partitionIndexes.length == 0 ? this.destroyCardTypePartitions() : this.resetPartition()

        this.calculateNewMargin()

        let lastCardEnum = this.cardInventory.cardViews[0].cardData.cardEnum
        let lastX = this.defaultCardMarginBetweenSameType

        for(const [index, card] of this.cardInventory.cardViews.entries()) {
            let newX = index == 0 ? this.defaultCardMarginBetweenSameType : this.getNewXOfCard(lastX, lastCardEnum, card)
            lastCardEnum = card.cardData.cardEnum

            if(partitionIndexes.includes(index)) {
                const currentPartitionIndex = partitionIndexes.findIndex((partitionIndex) => partitionIndex == index)
                newX = lastX + this.cardW + this.getPartitionWidth()
                this.setPartitionPosition(lastX, currentPartitionIndex)
            }

            lastX = newX

            if(animate) {
                const moveCardLaterally = new Point(newX, this.cardY)
                card.animationController.animateTo(moveCardLaterally)
                continue
            }
            card.x = newX
        }
    }

    private calculateNewMargin() {
        if(this.cardInventory.cardViews.length == 0) return
        this.resetBadgeForAllCards()

        this.cardMarginBetweenTypes = this.cardW
        this.cardMarginBetweenSameType = this.defaultCardMarginBetweenSameType

        const allCardEnums = this.cardInventory.getAllCardEnums()
        const numberOfUniqueCards = this.cardInventory.getAmountOfDifferentTypes()
        const numberOfSameType = allCardEnums.length - numberOfUniqueCards

        const cardPaddingFromSideOfContainer = (this.defaultCardMarginBetweenSameType) * 2
        const widthCausedByHavingUniqueCards = numberOfUniqueCards * this.cardMarginBetweenTypes
        const widthCausedBySimilarStackedCards = numberOfSameType * this.cardMarginBetweenSameType

        const numberOfPartitions = this.partitionViews.length
        const widthCausedByPartition = this.getPartitionWidth() * numberOfPartitions

        const totalWidthOfCardsInView = widthCausedByHavingUniqueCards + widthCausedBySimilarStackedCards + widthCausedByPartition + cardPaddingFromSideOfContainer

        const areCardsWithinContainerBounds = totalWidthOfCardsInView <= this.initialContainerWidth
        if(areCardsWithinContainerBounds) return

        const totalWidthDedicatedToCardsOfSameType = this.initialContainerWidth - widthCausedByHavingUniqueCards - widthCausedByPartition - cardPaddingFromSideOfContainer
        let newCardMarginBetweenSameType = totalWidthDedicatedToCardsOfSameType / numberOfSameType

        const doCardsOfDifferentTypeNeedToOverlap = newCardMarginBetweenSameType < 0

        if(newCardMarginBetweenSameType < 0.2 * this.cardMarginBetweenSameType) this.showBadgeForAllCards()
        if(doCardsOfDifferentTypeNeedToOverlap) {
            newCardMarginBetweenSameType = 0

            const totalWidthDedicatedToCardsOfDifferentType = this.initialContainerWidth - cardPaddingFromSideOfContainer - this.cardW * (1 + numberOfPartitions) - widthCausedByPartition
            this.cardMarginBetweenTypes = totalWidthDedicatedToCardsOfDifferentType / (numberOfUniqueCards - numberOfPartitions - 1)
        }
        this.cardMarginBetweenSameType = newCardMarginBetweenSameType
    }

    private getNewXOfCard(lastX: number, lastCardEnum: CardEnum, currentCardView: CardView): number {
        if(currentCardView.cardData.cardEnum == lastCardEnum) return lastX + this.cardMarginBetweenSameType

        return lastX + this.cardMarginBetweenTypes
    }

    getXOfType(cardEnum: CardEnum): number {
        if(this.cardInventory.cardViews.length == 0) return this.cardMarginBetweenSameType
        for(const cardView of this.cardInventory.cardViews) {
            if(cardView.cardData.cardEnum < cardEnum) continue
            return cardView.x
        }
        const lastCard = this.cardInventory.cardViews[this.cardInventory.cardViews.length - 1]
        return lastCard.x + this.cardMarginBetweenTypes
    }

    private setPartitionPosition(leftCardX: number, partitionIndex: number) {
        if(this.partitionViews[partitionIndex] == undefined) return

        const partitionPadding = this.defaultCardMarginBetweenSameType
        const partitionX = leftCardX + this.cardW + partitionPadding
        this.partitionViews[partitionIndex].x = partitionX
    }

    private getPartitionWidth(): number {
        if(this.partitionViews[0] == undefined) return 0

        const partitionPadding = this.defaultCardMarginBetweenSameType
        return this.partitionViews[0].width + partitionPadding * 2
    }

    private createPartitionWithGraphics(): PIXI.Graphics {
        const partitionH = 0.8 * this.cardH
        const partitionY = (this.initialContainerHeight - partitionH) / 2

        const graphics = new PIXI.Graphics()
        graphics.lineStyle(2, GameColor.getColorHexValue(GameColor.Color.Gray1), 0.3)
        graphics.moveTo(0, partitionY)
        graphics.lineTo(0, partitionY + partitionH) // End position

        this.addChild(graphics)

        return graphics
    }

    private getCardTypePartitionIndexes(): number[] {
        if(!this.partitionEnabled) return []
        const partitions = UIGameCardViewInventory.getPartitionCardIndexes(this.cardInventory.cardViews.map((card) => card.cardData))
        const removePartitionsIfOnlyOneCard = partitions.filter((partition) => partition != 0)
        return removePartitionsIfOnlyOneCard
    }

    private resetPartition() {
        this.destroyCardTypePartitions()
        const numberOfPartitions = this.getCardTypePartitionIndexes().length
        for(let i = 0; i < numberOfPartitions; i++) {
            this.partitionViews.push(this.createPartitionWithGraphics())
        }
    }

    private destroyCardTypePartitions() {
        for(const partition of this.partitionViews) {
            if(partition == undefined) return
            if(partition.parent == undefined) return
            partition.parent.removeChild(partition)
        }
        this.partitionViews.splice(0, this.partitionViews.length)
    }

    private resetBadgeForAllCards() {
        for(const cardView of this.cardInventory.cardViews) {
            cardView.showBadgeForDefaultCards()
        }
    }

    private showBadgeForAllCards() {
        for(const cardView of this.cardInventory.cardViews) {
            this.showCardCountForDevelopmentCardsLessThanTwo(cardView)
        }
    }

    private showCardCountForDevelopmentCardsLessThanTwo(cardView: CardView) {
        cardView.showBadgeForDefaultCards()

        const onlyShowCardsThatHaveMoreThanTwo = 2
        const cardCount = this.cardInventory.getCardTypeCount(cardView.cardData.cardEnum)
        if(cardCount < onlyShowCardsThatHaveMoreThanTwo) return
        cardView.showBadge()
    }

    closeUI() {
        if(this.parent == undefined) return
        this.parent.removeChild(this)
    }

    fadeInAllCardsExcludingCardsOfType(cardsToExclude: CardEnum[]) {
        for(const cardView of this.cardInventory.cardViews) {
            if(ArrayUtils.elementInArray(cardsToExclude, cardView.cardData.cardEnum)) continue
            cardView.fadeOutCard()
        }
    }

    flickerAllCardsOfType(cardEnums: CardEnum[]) {
        this.animateAllCardsOfType(cardEnums, UIGameFlickerAnimation)
    }

    shakeAllCardsOfType(cardEnums: CardEnum[]) {
        this.animateAllCardsOfType(cardEnums, UIGameShakeAnimation)
    }

    animateAllCardsOfType(cardEnums: CardEnum[], animationType: new (view: PIXI.Container) => UIGameAnimationBase) {
        for(const cardEnum of cardEnums) {
            const cardViews = this.cardInventory.getAllCardViewsOfType(cardEnum)
            cardViews.forEach((cardView: CardView) => {
                cardView.stopAnimation()
                cardView.setAnimation(new animationType(cardView.view))
            })
        }
    }

    stopAnimation() {
        for(const cardView of this.cardInventory.cardViews) {
            cardView.stopAnimation()
        }
    }

    enableAllCards() {
        this.cardInventory.cardViews.forEach((cardView: CardView) => cardView.fadeInCard())
    }

    disableCards(cardEnums: CardEnum[]) {
        const cardViews = this.cardInventory.cardViews.filter((cardView: CardView) => cardEnums.includes(cardView.cardData.cardEnum))
        for(const cardView of cardViews) {
            cardView.fadeOutCard()
        }
    }

    getDisabledCards(): CardEnum[] {
        const disabledCards: CardEnum[] = []
        for(const cardView of this.cardInventory.cardViews) {
            const isCardEnabled = cardView.view.tint == GameColor.getColorHexValue(GameColor.Color.White)
            if(isCardEnabled) continue
            disabledCards.push(cardView.cardData.cardEnum)
        }
        return disabledCards
    }
}
